import { Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

interface PreviewToolbarProps {
  showSampleData: boolean;
  onToggleSampleData: (value: boolean) => void;
  hasUnsavedChanges: boolean;
  onExportPDF: () => Promise<void>;
  zoom: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

export function PreviewToolbar({
  showSampleData,
  onToggleSampleData,
  hasUnsavedChanges,
  onExportPDF,
  zoom,
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
}: Readonly<PreviewToolbarProps>) {
  const [isExporting, setIsExporting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const handleExportClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      return;
    }
    handleExport();
  };

  const handleExport = async () => {
    setShowUnsavedDialog(false);
    setIsExporting(true);

    try {
      await onExportPDF();
      toast.success('Preview PDF downloaded');
    } catch (error) {
      toast.error('PDF generation failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="h-12 bg-muted/50 border-b flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Switch
            id="sample-data-toggle"
            checked={showSampleData}
            onCheckedChange={onToggleSampleData}
          />
          <label
            htmlFor="sample-data-toggle"
            className="text-sm cursor-pointer whitespace-nowrap text-foreground select-none"
          >
            Preview with sample data
          </label>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!canZoomOut}
            onClick={onZoomOut}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!canZoomIn}
            onClick={onZoomIn}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportClick}
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </>
          )}
        </Button>
      </div>

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Your template has unsaved changes. The exported PDF will reflect
              your current unsaved state. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleExport}>
              Export anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
